import styles from '../Columns/columns.module.scss';
import { RESIZABLE_COLUMNS } from './resizableConfig';

type ColumnWidths = Record<string, number>;

/**
 * Get the CSS variable name for a column key
 * e.g., 'title' -> 'column_title_width'
 */
const getColumnCssKey = (columnKey: string): string => {
  return `column_${columnKey}_width`;
};

const resizableColumnCssKeys = new Set(
  RESIZABLE_COLUMNS.map((col) => getColumnCssKey(col.key))
);

/**
 * Base table width excludes resizable columns (they're calculated separately).
 */
const getBaseTableWidth = (): number => {
  return Object.entries(styles)
    .filter(([key]) => key.startsWith('column_') && key.endsWith('_width'))
    .filter(([key]) => !resizableColumnCssKeys.has(key))
    .map(([, value]) => Number(value))
    .reduce((sum, width) => sum + width, 0);
};

const getResizableColumnsWidth = (columnWidths: ColumnWidths): number => {
  return RESIZABLE_COLUMNS.reduce((sum, col) => {
    const width = columnWidths[col.key] ?? col.defaultWidth;
    return sum + width;
  }, 0);
};

/**
 * Calculate total table width
 * Base width + resizable columns width
 */
export const getTotalTableWidth = (columnWidths: ColumnWidths): number => {
  return getBaseTableWidth() + getResizableColumnsWidth(columnWidths);
};

/**
 * Returns stored width or default width for resizable columns.
 * Returns undefined for non-resizable columns.
 */
export const getColumnWidth = (
  columnKey: string,
  columnWidths: ColumnWidths
): number | undefined => {
  const resizableColumn = RESIZABLE_COLUMNS.find(
    (col) => col.key === columnKey
  );
  if (!resizableColumn) return undefined;

  // Return stored width or default width (same logic as getResizableColumnsWidth)
  return columnWidths[columnKey] ?? resizableColumn.defaultWidth;
};
